import { useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Per-user theme hook with backend persistence.
 *
 * - Per-user storage key: `theme:{role}:{user_id}` (also serves as offline cache).
 * - On rider login, fetch backend preference (`/api/settings/appearance/{user_id}`)
 *   and apply it; falls back to localStorage cache then 'device'.
 * - For non-rider users (or no user) -> always force light mode.
 *
 * Pass the currently authenticated user (or null).
 */
export const useTheme = (user) => {
  useEffect(() => {
    // One-time cleanup: remove legacy global 'theme' key from before per-user theming.
    try { localStorage.removeItem('theme'); } catch (_) { /* noop */ }

    // Dark mode is mocked/deferred: guarantee the 'dark' class can never linger
    // on <html>, even if injected externally. Idempotent install.
    installDarkModeGuard();

    const role = user?.role;
    const id = user?.id;

    // Riders are the only role with a theme toggle UI today.
    if (!user || !id || role !== 'rider') {
      applyTheme('light');
      return;
    }

    const themeKey = `theme:${role}:${id}`;

    // 1) Apply cached preference immediately for instant UI (no flash)
    const cachedTheme = localStorage.getItem(themeKey) || 'device';
    applyTheme(cachedTheme);

    // 2) Fetch authoritative preference from backend and apply if different
    let cancelled = false;
    fetch(`${API_URL}/api/settings/appearance/${id}`)
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        const remoteTheme = data?.preferences?.theme;
        if (remoteTheme && ['light', 'dark', 'device'].includes(remoteTheme) && remoteTheme !== cachedTheme) {
          localStorage.setItem(themeKey, remoteTheme);
          applyTheme(remoteTheme);
        }
      })
      .catch(() => { /* offline or first-time user, keep cached */ });

    // 3) Listen for system theme changes when device mode is selected
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = () => {
      const currentTheme = localStorage.getItem(themeKey) || 'device';
      if (currentTheme === 'device') {
        applyTheme('device');
      }
    };
    mediaQuery.addEventListener('change', handleSystemThemeChange);

    return () => {
      cancelled = true;
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, [user?.id, user?.role]);
};

export const getThemeKey = (user) => {
  if (!user || !user.id || !user.role) return null;
  return `theme:${user.role}:${user.id}`;
};

export const applyTheme = (_selectedTheme) => {
  // Dark mode is temporarily mocked — the rider app is light-only for now.
  // (Full dark theme will be implemented later.) Always render light so no
  // screen shows unreadable dark-on-dark / white-on-white text.
  document.documentElement.classList.remove('dark');
};

// While dark mode is mocked, defensively strip the 'dark' class if anything
// (external scripts, devtools, stale state) ever adds it. Installed once.
let _darkGuardInstalled = false;
export const installDarkModeGuard = () => {
  if (_darkGuardInstalled || typeof MutationObserver === 'undefined') return;
  _darkGuardInstalled = true;
  const root = document.documentElement;
  root.classList.remove('dark');
  const observer = new MutationObserver(() => {
    if (root.classList.contains('dark')) root.classList.remove('dark');
  });
  observer.observe(root, { attributes: true, attributeFilter: ['class'] });
};

/**
 * Save the chosen theme both locally (instant) and remotely (cross-device).
 * Returns a promise that resolves to { success, remote } where `remote` is true
 * if the backend save succeeded.
 */
export const saveThemePreference = async (user, theme) => {
  if (!['light', 'dark', 'device'].includes(theme)) {
    return { success: false, remote: false, error: 'invalid theme' };
  }
  // Always save locally first for offline support
  const key = getThemeKey(user);
  if (key) {
    try { localStorage.setItem(key, theme); } catch (_) { /* noop */ }
  }
  applyTheme(theme);

  // Try to persist to backend (best-effort; works for all roles that have a user_id)
  if (user?.id) {
    try {
      const res = await fetch(`${API_URL}/api/settings/appearance/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ theme })
      });
      const data = await res.json().catch(() => ({}));
      return { success: true, remote: !!data?.success };
    } catch (_) {
      return { success: true, remote: false };
    }
  }
  return { success: true, remote: false };
};

/**
 * Reset to light mode. Call on logout so the next user lands on a clean slate.
 */
export const resetThemeToLight = () => {
  document.documentElement.classList.remove('dark');
};
